import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActiveResponse,
    disableSubclass,
    editSubclass,
    enableSubclass,
    getSubclassPaginated,
    newSubclass,
    SubClassModel,
} from "../../../services/decisionFlow";
import { initialPagination, PaginatedResponse } from "../../../domain/models/paginated-response.model";

type State = {
    open: boolean;
    subclass?: SubClassModel;
};

/** Estado, fetch e handlers da administração de subclasses. */
export function useManageSubclass() {
    const [state, setState] = useState<State>({ open: false });
    const [list, setList] = useState<PaginatedResponse<SubClassModel>>(initialPagination);
    const [modalName, setModalName] = useState("");
    const [search, setSearch] = useState("");

    const filteredRows = useMemo(() => {
        if (!search) return list.data;
        return list.data.filter((row) => row.name.toLowerCase().includes(search.toLowerCase()));
    }, [list.data, search]);

    const fetchList = useCallback(async (newPage: number = 0, newSize: number = 10) => {
        const result = await getSubclassPaginated(newPage + 1, newSize);
        const response = result.data as PaginatedResponse<SubClassModel>;
        setList(response);
    }, []);

    useEffect(() => {
        fetchList(0, 10);
    }, [fetchList]);

    const handlePageChange = useCallback(
        async (newPage: number) => {
            await fetchList(newPage - 1, list.pagination.perPage);
        },
        [fetchList, list.pagination.perPage]
    );

    const handlePageSizeChange = useCallback(
        async (newSize: number) => {
            await fetchList(0, newSize);
        },
        [fetchList]
    );

    const handleEnableActive = useCallback(
        async (active: ActiveResponse) => {
            if (active.enabled) {
                await disableSubclass(active.id);
                await fetchList();
            } else {
                await enableSubclass(active.id);
                await fetchList();
            }
        },
        [fetchList]
    );

    const handleNewActive = useCallback(
        async (name: string, id?: number) => {
            if (id) {
                await editSubclass(id, name);
            } else {
                await newSubclass(name);
            }
            await fetchList();
            setState({ open: false });
        },
        [fetchList]
    );

    const openCreate = () => {
        setModalName("");
        setState({ open: true });
    };

    const openEdit = (subclass: SubClassModel) => {
        setModalName(subclass.name);
        setState({ open: true, subclass: { ...subclass } });
    };

    const closeModal = () => {
        setState({ open: false });
    };

    const isEditing = Boolean(state.subclass);

    return {
        state,
        list,
        modalName,
        setModalName,
        search,
        setSearch,
        filteredRows,
        isEditing,
        handlePageChange,
        handlePageSizeChange,
        handleEnableActive,
        handleNewActive,
        openCreate,
        openEdit,
        closeModal,
    };
}
